/** @file Paints one frame: viewport, premultiplied clear, chunk sync, camera rows, then every draw. */
import type { Camera, RenderBuffers } from '@pose2d/geometry/types';
import { backgroundClear } from './backgroundClear';
import { clipTransform } from './clipTransform';
import type { Gpu } from './initGpu';
import { issueDraws } from './issueDraws';

/** Per-renderer scratch so a frame allocates nothing: clip rows (6 floats) and clear colour (4 floats). */
export interface FrameScratch {
  rows: Float32Array;
  clear: Float32Array;
}

/** Renders `chunks` through `camera` over `background` (straight `math/color.pack` word) into the drawing buffer. */
export function paintFrame(gl: WebGL2RenderingContext, gpu: Gpu, chunks: readonly RenderBuffers[], camera: Camera, background: number, scratch: FrameScratch): void {
  const c = backgroundClear(scratch.clear, background);
  gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
  gl.clearColor(c[0] ?? 0, c[1] ?? 0, c[2] ?? 0, c[3] ?? 0);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gpu.chunks.sync(chunks);
  gl.useProgram(gpu.program.program);
  const rows = clipTransform(scratch.rows, camera);
  gl.uniform3fv(gpu.program.row0, rows, 0, 3);
  gl.uniform3fv(gpu.program.row1, rows, 3, 3);
  issueDraws(gl, chunks, gpu.chunks, gpu.pages);
}
